import {
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  input,
  signal,
} from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { MediaDocument } from '../../../../core/model/media-document.model';
import { HoverGradientDirective } from '../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../shared/directives/progress-click.directive';
import { CdnPipe } from '../shared/pipes/cdn.pipe';

@Component({
  selector: 'app-gallery',
  imports: [
    NgOptimizedImage,
    NgClass,
    CdnPipe,
    ReactiveFormsModule,
    NgxTolgeeModule,
    HoverGradientDirective,
    ProgressClickDirective,
    RouterLink
  ],
  template: `
    @if (password() && !authorized()) {
      <div class="flex flex-wrap mb-4">
        <div class="w-full">
          <label class="text-gray-500 text-md font-semibold pb-2 block" for="address">
            {{ 'password' | translate }} <span class="text-red-600">*</span>
          </label>
          <input
            [formControl]="passwordControl"
            class="appearance-none block w-full text-gray-700 border border-gray-200 rounded-lg py-4 px-4 mb-3
            leading-tight focus:outline-none focus:bg-white focus:border-gray-500 disabled:bg-gray-100"
            type="text">
        </div>
        <span class="text-gray-500">{{ 'protectedAccess' | translate }}
          <svg routerLink="/press" fragment="form" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
               stroke-width="1.5" stroke="currentColor" class="size-4 inline">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25
            0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"/>
            </svg>
        </span>
        <button type="button"
                appHoverGradient
                [disabled]="passwordControl.invalid"
                [appProgressClick]="auth"
                class="big-btn flex justify-center items-center gap-2">
          {{ 'validate' | translate }}
          <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#ffffff">
            <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
            <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
            <g id="SVGRepo_iconCarrier">
              <g clip-path="url(#clip0_429_11249)">
                <path d="M20 7.00018L10 17.0002L5 12.0002" stroke="#ffffff" stroke-width="2.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"></path>
              </g>
              <defs>
                <clipPath id="clip0_429_11249">
                  <rect width="24" height="24" fill="white"></rect>
                </clipPath>
              </defs>
            </g>
          </svg>
        </button>
      </div>
    } @else {
      <ul role="list"
          class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2 sm:gap-x-6 xl:gap-x-8 mx-4 md:mx-8"
      >
        @for (image of imagesToDisplay(); let index = $index; track image) {
          <li class="group relative"
              [ngClass]="{'sm:col-span-2': index % 3 === 0 }">
            <div class="aspect-h-7 aspect-w-10 block w-full overflow-hidden rounded-lg bg-gray-100
                      focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2
                      focus-within:ring-offset-gray-100">
              @if (downloadable()) {
                <a [href]="image.media | cdn : 'image'" download>
                  <img class="pointer-events-none object-cover"
                       [alt]="image.alt"
                       [ngSrc]="image.media"
                       fill=""
                       [sizes]="index % 3 === 0 ?
                    '80vw':
                    '(max-width: 768) 80vw, 50vw'"
                  >
                  <div class="absolute opacity-0 group-hover:opacity-50 top-0 bottom-0 left-0 right-0 bg-black
                  text-white flex items-center justify-center">
                    <p class="font-semibold uppercase">{{ image.alt }}</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                      stroke="currentColor" class="w-16 h-16 hover:text-bleuCaliceo-300">
                      <path stroke-linecap="round" stroke-linejoin="round"
                            d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0
                        0L7.5 12m4.5 4.5V3"/>
                    </svg>
                  </div>
                </a>
              } @else {
                <img class="pointer-events-none object-cover"
                     [alt]="image.alt"
                     [ngSrc]="image.media"
                     fill=""
                     [sizes]="index % 3 === 0 ?
                    '80vw':
                    '(max-width: 768) 80vw, 50vw'"
                >
              }
            </div>
          </li>
        }
      </ul>
    }
  `
})

/**
 * Gallery component
 */
export class GalleryComponent {
  images = input<MediaDocument[]>([]);
  imagesToDisplay = computed(() => {
    const images = this.images();
    if (images.length) {
      return images;
    }
    return [ new MediaDocument({ alt: '', media: 'assets/images/placeholder.webp' }) ]
  })
  downloadable = input<boolean>(false);
  password = input<string>();
  passwordControl = new FormControl('', { nonNullable: true, validators: [ Validators.required ] });
  authorized = signal(false);

  auth = async (): Promise<void> => {
    await new Promise<void>(resolve => setTimeout(resolve, 1000));
    if (this.passwordControl.value === this.password()) {
      this.authorized.set(true)
    }
  }
}
